import { useEffect, useId, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useNavTitle } from '../lib/nav'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { capture } from '@region13/analytics'
import { useToast, type ToastApi, Button } from '@region13/ui'
import { PHONE_TAP } from '../components/ui'
import {
  teamLabel,
  type FeasibleVenue,
  type PracticePairing,
  type PracticePriorityAxis,
  type PracticeWeekday,
} from '@region13/shared-types'
import { api, ApiError, type CoachTeamWithGames, type PracticePrefsInput } from '../lib/api'
import {
  allDaysBlockedWarning, EXCLUDED_TEAM_MESSAGE, NOT_OPEN_TEAM_MESSAGE, rankableVenues, reachableWeekdays,
  timeRangeProblem, unsatisfiablePairs, unsatisfiableWarning,
} from '../lib/practicePrefsCopy'
import { useIsAdmin } from '../lib/useIsAdmin'
import { Card } from '../components/ui/Card'
import { TeamScope } from '../components/TeamScope'
import { PracticeSlotCard } from '../components/PracticeSlotCard'
import { DayPreferencePicker } from '../components/DayPreferencePicker'
import { VenuePreferencePicker } from '../components/VenuePreferencePicker'
import { UpperDivisionFields } from '../components/UpperDivisionFields'
import { ResubmissionBanner } from '../components/ResubmissionBanner'

export const Route = createFileRoute('/practice')({ component: Practice })

const inputClass =
  'min-h-[44px] rounded-lg border border-line bg-surface px-3 text-sm text-ink ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-maroon/30 focus:border-brand-maroon'

/** A coach with two teams sees BOTH cards stacked on one page (`TeamScope`
 *  maps every team), and an error toast never auto-dismisses - errors are
 *  durable so they can be read and copied (`packages/ui` Toast). So an
 *  unlabeled failure banner outlives the card that raised it and reads as a
 *  refusal of whichever card the coach is looking at: on 2026-08-24 a tester
 *  whose B10-01 save was refused was looking at his B12-02 card, which had
 *  saved a minute earlier, and reported the rule as applying across teams.
 *
 *  ⚠️ `team` is absent for the coach-level pairing save (PairingCard), which is
 *  the one save on this page that has no team - so it MUST override `fallback`
 *  too. Sharing the default there would put an unprefixed "Could not save
 *  practice preferences." beside two prefixed team banners, which reads as a
 *  team save whose label went missing, and names the wrong thing besides: that
 *  mutation saves the back-to-back wish, not a team's preferences. */
function toastSaveError(toast: ToastApi, err: unknown, opts: { team?: string; fallback?: string } = {}) {
  const message = err instanceof ApiError
    ? err.message
    : opts.fallback ?? 'Could not save practice preferences.'
  toast.error(opts.team ? `${opts.team}: ${message}` : message)
}

/** Local, editable draft of a team's practice preferences — separate from the
 *  server `PracticePreference` shape so blank inputs (no time / no override)
 *  can round-trip through plain strings before being parsed back to null. */
interface PrefsDraft {
  dayPref: PracticeWeekday[]
  /** Days the coach says do not work at all (spec D3). Disjoint from
   *  `dayPref` by construction: `cycleDay` is the only thing that edits
   *  either list, and it maintains that invariant. */
  dayBlocked: PracticeWeekday[]
  /** Ranked days for a UD team's second weekly practice (spec D4). Null for
   *  every team on this form; the Upper Division form (Task 7) is what
   *  actually sets this. */
  dayPref2: PracticeWeekday[] | null
  timeEarliest: string
  timeLatest: string
  /** Soft target inside the hard earliest/latest bounds; blank means no
   *  preference, same round-trip-through-a-string convention as the two
   *  fields above. */
  timePreferred: string
  /** Which of day-of-week or venue to sacrifice first. Unlike the time/venue
   *  fields, this always carries a real value - there is no blank state. */
  priorityAxis: PracticePriorityAxis
  venuePref: string[]
  note: string
}

const BLANK_DRAFT: PrefsDraft = {
  dayPref: [],
  dayBlocked: [],
  dayPref2: null,
  timeEarliest: '',
  timeLatest: '',
  timePreferred: '',
  priorityAxis: 'day',
  venuePref: [],
  note: '',
}

/** The earliest-start / latest-end time inputs — split out of PracticeForm so
 *  its render stays focused on the two ranked pickers.
 *
 *  `errorId` is the id of the blocking message when THESE two fields are the
 *  contradiction, and null otherwise: a reader who tabs back to an input has
 *  to be told it is the offender, and the disabled Save button alone cannot
 *  tell them that. Null rather than a boolean because the same value is both
 *  the flag and the IDREF, so they can never disagree. */
function TimeRangeFields({ draft, onPatch, errorId }: {
  draft: PrefsDraft
  onPatch: (fields: Partial<PrefsDraft>) => void
  errorId: string | null
}) {
  const flagged = { 'aria-invalid': errorId ? true : undefined, 'aria-describedby': errorId ?? undefined }
  return (
    <div className="flex gap-3">
      <label className="flex flex-1 flex-col gap-1 text-xs font-semibold text-ink-soft">
        Earliest start
        <input type="time" value={draft.timeEarliest} onChange={(e) => onPatch({ timeEarliest: e.target.value })} className={inputClass} {...flagged} />
      </label>
      <label className="flex flex-1 flex-col gap-1 text-xs font-semibold text-ink-soft">
        Latest end
        <input type="time" value={draft.timeLatest} onChange={(e) => onPatch({ timeLatest: e.target.value })} className={inputClass} {...flagged} />
      </label>
    </div>
  )
}

/** Soft preferred-start time plus the day-vs-field sacrifice axis - both sit
 *  on top of the hard earliest/latest bounds from TimeRangeFields, so they're
 *  split out the same way. Preferred start is optional (blank means no
 *  preference); priorityAxis always carries a real value. */
function PreferenceAxisFields({ draft, onPatch, errorId }: {
  draft: PrefsDraft
  onPatch: (fields: Partial<PrefsDraft>) => void
  /** Set only when the PREFERRED start is the contradiction; see
   *  TimeRangeFields for why this is an id rather than a boolean. */
  errorId: string | null
}) {
  return (
    <>
      <label className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-ink-soft">Preferred start (optional)</span>
        <input
          type="time"
          className={inputClass}
          value={draft.timePreferred}
          onChange={(e) => onPatch({ timePreferred: e.target.value })}
          aria-invalid={errorId ? true : undefined}
          aria-describedby={errorId ?? undefined}
        />
        <span className="text-xs text-ink-soft">
          Where inside your earliest and latest you would rather be. We cannot promise it.
        </span>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-ink-soft">If we cannot give you both, which matters more</span>
        <select
          className={inputClass}
          value={draft.priorityAxis}
          onChange={(e) => onPatch({ priorityAxis: e.target.value as PracticePriorityAxis })}
        >
          <option value="day">The day of the week</option>
          <option value="field">The field</option>
        </select>
      </label>
    </>
  )
}

/** The free-text note - the form's last scalar field, split out for the same
 *  reason as TimeRangeFields. Practice length is not a coach choice (the
 *  admin sets it per division), so there is no override input here. */
function NotesFields({ draft, onPatch }: { draft: PrefsDraft; onPatch: (fields: Partial<PrefsDraft>) => void }) {
  return (
    <label className="flex flex-col gap-1 text-xs font-semibold text-ink-soft">
      Notes for the scheduler
      <textarea rows={3} value={draft.note} onChange={(e) => onPatch({ note: e.target.value })} className={inputClass} />
    </label>
  )
}

/** The coach-level back-to-back wish's control: a plain controlled select.
 *  Pure and network-free - `PairingCard` below owns the fetch/save, so this
 *  can be render-tested on its own with no query client involved. Split out
 *  purely to keep `PairingCard` focused on data, not markup. */
function PairingField({
  pairing,
  onChange,
}: {
  pairing: PracticePairing | null
  onChange: (v: PracticePairing | null) => void
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-ink-soft">You coach more than one team</span>
      <select
        className={inputClass}
        value={pairing ?? ''}
        onChange={(e) => onChange((e.target.value || null) as PracticePairing | null)}
      >
        <option value="">No preference</option>
        <option value="back_to_back">Back to back, so I make one trip</option>
        <option value="separate_days">On separate days</option>
      </select>
      <span className="text-xs text-ink-soft">
        This applies to all of your teams, so you only answer it once.
      </span>
    </label>
  )
}

/** Builds the save payload from the draft, narrowing `venuePref` to what the
 *  lit filter (spec D6) currently allows. Without this, a coach who ranked a
 *  venue before asking for back-to-back practices - or before the filter
 *  existed - could save an unlit venue the picker had already told them, by
 *  name, was no longer offered: the record would contradict the notice the
 *  coach was shown. Narrowing here, not just on-screen, is what closes that
 *  untouched-draft case (spec D6 follow-up).
 *
 *  For a UD team (Task 7), `dayPref2` is sent for real and the three time
 *  fields are sent as `null` regardless of the draft's stored values: UD's
 *  form renders no time inputs, so a UD draft's time strings are stale at
 *  best (left over from before the team's variant was known, say) and must
 *  never round-trip to the server as if the coach had answered them. */
function toPayload(
  draft: PrefsDraft,
  venues: FeasibleVenue[],
  pairing: PracticePairing | null,
  isUpperDivision: boolean,
): PracticePrefsInput {
  const offered = rankableVenues(venues, pairing)
  const venuePref = draft.venuePref.filter((slug) => offered.some((v) => v.slug === slug))
  return {
    dayPref: draft.dayPref,
    dayPref2: isUpperDivision ? draft.dayPref2 ?? [] : null,
    dayBlocked: draft.dayBlocked,
    timeEarliest: isUpperDivision ? null : draft.timeEarliest || null,
    timeLatest: isUpperDivision ? null : draft.timeLatest || null,
    timePreferred: isUpperDivision ? null : draft.timePreferred || null,
    priorityAxis: draft.priorityAxis,
    venuePref,
    note: draft.note || null,
  }
}

/** One team's practice-preferences form: loads the current submission (if
 *  any) and this team's feasible venues (age-eligible + a long-enough permit
 *  window - already filtered server-side), then PUTs the edited draft back.
 *  The coach-level back-to-back wish (`PairingCard`) is NOT rendered here -
 *  it is a different resource (not team-scoped) and is hoisted to the route
 *  wrapper so it renders once for the coach, not once per team card. This
 *  form still needs to READ that wish (to know which venues the lit filter
 *  allows), so `teamCount` is threaded down the same way `PairingCard` gets
 *  it, and the query below is gated the same way - `enabled: asksPairing` -
 *  since the question is meaningless with one team. */
export function PracticePrefsForm({ team, teamCount }: { team: CoachTeamWithGames; teamCount: number }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  // An operator's `teamCount` is the whole season (GET /api/coach/teams returns
  // every team for a superadmin), so `teamCount > 1` alone would make the lit
  // filter apply to every team they inspect - narrowing `venuePref` at save
  // time against a pairing row stored under the OPERATOR's email. See
  // `PairingCard`, which hides the question from them for the same reason.
  const isAdmin = useIsAdmin()
  const asksPairing = teamCount > 1 && !isAdmin

  const prefsQ = useQuery({ queryKey: ['practice-prefs', team.externalId], queryFn: () => api.practicePrefs(team.externalId) })
  const variant = prefsQ.data?.variant ?? 'standard'
  const isUpperDivision = variant === 'upper_division'
  const feasQ = useQuery({
    queryKey: ['practice-feasibility', team.externalId],
    queryFn: () => api.practiceFeasibility(team.externalId),
  })
  const venues = feasQ.data?.venues ?? []
  // Same query key AND the same `enabled` gate `PairingCard` uses, so a
  // multi-team coach's forms share its cached result and add no fetch of
  // their own; a single-team coach never fetches here either, matching
  // `PairingCard`'s own invariant (the question is meaningless with one team),
  // and neither does an operator viewing as a coach, who is never asked it.
  const pairingQ = useQuery({ queryKey: ['practice-pairing'], queryFn: api.practicePairing, enabled: asksPairing })
  // Read as null whenever the question was not asked, rather than trusting the
  // shared cache to be empty: the lit filter must never narrow a team's venue
  // list on the strength of a wish this coach was never shown.
  const pairing = asksPairing ? pairingQ.data?.pairing ?? null : null

  const [draft, setDraft] = useState<PrefsDraft>(BLANK_DRAFT)
  const patch = (fields: Partial<PrefsDraft>) => setDraft((d) => ({ ...d, ...fields }))
  // Per-instance, because a multi-team coach renders one of these cards per
  // team: a hardcoded id would point every disabled Save at the first card's
  // error.
  const timeErrorId = useId()

  // `survey_started` fires once per team card, the moment this team's own
  // form is actually about to render — not on a loading skeleton, a failed
  // load, or the excluded/not-open sentences below, none of which show a
  // question a coach could start answering. A ref rather than a query-key
  // dependency: the effect must fire at most once per mount, even if the
  // queries above refetch (e.g. the post-save `invalidateQueries`).
  const startedRef = useRef(false)
  const formReady =
    !prefsQ.isLoading &&
    !feasQ.isLoading &&
    !(prefsQ.isError && prefsQ.data === undefined) &&
    variant !== 'excluded' &&
    variant !== 'not_open'
  useEffect(() => {
    if (!formReady || startedRef.current) return
    startedRef.current = true
    // No season id: it is never surfaced to this client (the server resolves
    // "the live season" itself), so there is nothing safe to send but the
    // fact that a form was started.
    capture('survey_started')
  }, [formReady])

  // Repopulate the draft whenever the loaded prefs (or the selected team)
  // change, so switching teams never leaves another team's edits behind.
  useEffect(() => {
    const p = prefsQ.data?.prefs
    setDraft(
      p
        ? {
            dayPref: p.dayPref,
            dayBlocked: p.dayBlocked,
            dayPref2: p.dayPref2,
            timeEarliest: p.timeEarliest ?? '',
            timeLatest: p.timeLatest ?? '',
            timePreferred: p.timePreferred ?? '',
            priorityAxis: p.priorityAxis,
            venuePref: p.venuePref,
            note: p.note ?? '',
          }
        : BLANK_DRAFT,
    )
  }, [prefsQ.data, team.externalId])

  const saveMutation = useMutation({
    mutationFn: () => api.savePracticePrefs(team.externalId, toPayload(draft, venues, pairing, isUpperDivision)),
    onSuccess: () => {
      toast.success('Practice preferences saved.')
      void queryClient.invalidateQueries({ queryKey: ['practice-prefs', team.externalId] })
      // team_count only — no answers. The draft's day/venue/time picks are
      // free-form-adjacent coach input the PII rules forbid outright, and
      // there is no season id to send (see `survey_started` above).
      capture('survey_submitted', { team_count: teamCount })
    },
    onError: (err: unknown) => toastSaveError(toast, err, { team: team.externalId }),
  })

  if (prefsQ.isLoading) {
    return (
      <Card as="section">
        <p role="status" className="text-sm text-ink-soft">
          Loading preferences…
        </p>
      </Card>
    )
  }

  // A failed prefs GET must NOT fall through to the form. `variant` would
  // default to 'standard' and the effect above would install BLANK_DRAFT, so a
  // coach with a real submission on file would be shown an empty form with
  // Save enabled and no resubmission banner: pressing it would overwrite their
  // answers, record a revision, and reset `submitted_at` - the placement-order
  // queue key - costing the team its place in line. For an Upper Division team
  // the standard form would render as well, so `toPayload` would send
  // `dayPref2: null` and wipe a second-practice ranking the coach cannot even
  // see. This branch mirrors `feasQ`'s below deliberately: the two queries gate
  // the same form and must fail the same way.
  //
  // The `data === undefined` half narrows this to a query that has NEVER
  // succeeded. TanStack keeps the last good `data` when a REFETCH fails, so on
  // `isError` alone the post-save `invalidateQueries` 502ing would replace a
  // just-saved form with "Nothing has been changed" - which is false, the save
  // landed. With data in hand the form is still safe to render: it is the
  // coach's own answers, not a blank draft.
  if (prefsQ.isError && prefsQ.data === undefined) {
    return (
      <Card as="section">
        <h2 className="font-semibold text-ink">
          {teamLabel({ designation: team.externalId, coachName: team.coachName, teamName: team.name })}
        </h2>
        <p role="alert" className="text-sm text-brand-red-dark">
          Couldn't load this team's practice preferences, so the form is not shown. Nothing has been changed. Please
          reload the page and try again.
        </p>
      </Card>
    )
  }

  // Preschool (D10) and no-longer-fielded (D11) teams get a sentence and no
  // controls at all - not a disabled form. The server-side refusal (Task 4)
  // is what actually enforces this; this is defense in depth, and it is
  // per-team rather than hiding the whole route because a coach with both a
  // 4U and a 10U team still has to reach the 10U form.
  if (variant === 'excluded') {
    return (
      <Card as="section">
        <h2 className="font-semibold text-ink">
          {teamLabel({ designation: team.externalId, coachName: team.coachName, teamName: team.name })}
        </h2>
        <p className="text-sm text-ink-soft">{EXCLUDED_TEAM_MESSAGE}</p>
      </Card>
    )
  }

  // The division has not been opened for collection yet. Same shape as the
  // excluded branch (a sentence, no controls) but a different sentence: this
  // team will get a form, just not now. The server-side refusal in the coach
  // worker is what actually enforces it; this is defense in depth, and it is
  // per-team because a coach may hold one team in an open division and one in
  // a closed one.
  if (variant === 'not_open') {
    return (
      <Card as="section">
        <h2 className="font-semibold text-ink">
          {teamLabel({ designation: team.externalId, coachName: team.coachName, teamName: team.name })}
        </h2>
        <p className="text-sm text-ink-soft">{NOT_OPEN_TEAM_MESSAGE}</p>
      </Card>
    )
  }

  // UD renders no time inputs and `toPayload` sends all three fields as null,
  // so a UD draft's stale time strings must not block its save.
  const timeProblem = isUpperDivision ? null : timeRangeProblem(draft)
  const rangeErrorId = timeProblem?.fault === 'inverted' ? timeErrorId : null
  const preferredErrorId = timeProblem && timeProblem.fault !== 'inverted' ? timeErrorId : null
  const reachableDays = reachableWeekdays(venues, draft.venuePref)
  const dayWarning = allDaysBlockedWarning({ ranked: draft.dayPref, blocked: draft.dayBlocked }, reachableDays)
  // Computed from the venues the coach may actually rank (spec D6's lit
  // filter), so a venue already dropped for a back-to-back coach is not
  // warned about twice. UD's ranked days are the union of both practices.
  const offered = rankableVenues(venues, pairing)
  const combinationWarning = unsatisfiableWarning(
    unsatisfiablePairs(offered, draft.venuePref, [...draft.dayPref, ...(draft.dayPref2 ?? [])]),
    offered,
  )

  return (
    <Card as="section">
      <div>
        <h2 className="font-semibold text-ink">
          {teamLabel({ designation: team.externalId, coachName: team.coachName, teamName: team.name })}
        </h2>
        <p className="text-sm text-ink-soft">{team.division}</p>
      </div>

      <PracticeSlotCard teamExternalId={team.externalId} />

      {isUpperDivision ? (
        <UpperDivisionFields draft={draft} onPatch={patch} reachable={reachableDays} />
      ) : (
        <>
          <DayPreferencePicker
            label="Preferred practice days"
            value={{ ranked: draft.dayPref, blocked: draft.dayBlocked }}
            onChange={(v) => patch({ dayPref: v.ranked, dayBlocked: v.blocked })}
            reachable={reachableDays}
          />
          <TimeRangeFields draft={draft} onPatch={patch} errorId={rangeErrorId} />
          {/* Between the two groups on purpose, so it sits next to whichever
              input is at fault: below PreferenceAxisFields a range error is a
              labeled radio group (and on a phone, a scroll) away from the two
              times it is about. Red-dark, not the maroon the two warnings
              below use: on this form maroon warns and still saves, red-dark
              blocks. */}
          {timeProblem && (
            <p id={timeErrorId} role="alert" className="text-xs text-brand-red-dark">
              {timeProblem.message}
            </p>
          )}
          <PreferenceAxisFields draft={draft} onPatch={patch} errorId={preferredErrorId} />
        </>
      )}
      {dayWarning && (
        <p role="alert" className="text-xs text-brand-maroon">
          {dayWarning}
        </p>
      )}

      {feasQ.isLoading ? (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-ink-soft">Preferred venues (in order)</span>
          <p className="text-sm text-ink-soft">Loading venues…</p>
        </div>
      ) : feasQ.isError && feasQ.data === undefined ? (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-ink-soft">Preferred venues (in order)</span>
          <p role="alert" className="text-sm text-brand-red-dark">
            Couldn't load the venue list. Please try again.
          </p>
        </div>
      ) : (
        <VenuePreferencePicker
          venues={venues}
          value={draft.venuePref}
          onChange={(venuePref) => patch({ venuePref })}
          pairing={pairing}
          teamAge={feasQ.data?.teamAge ?? null}
        />
      )}

      <NotesFields draft={draft} onPatch={patch} />

      {combinationWarning && (
        <p role="alert" className="text-xs text-brand-maroon">
          {combinationWarning}
        </p>
      )}

      <ResubmissionBanner prefs={prefsQ.data?.prefs ?? null} />

      {/* `aria-disabled` rather than `disabled` while the times contradict each
          other: a native disabled button leaves the tab order, so the coach who
          most needs `aria-describedby` - keyboard or screen reader - can never
          land on it to read why Save is unavailable. It is `disabled` for real
          while the PUT is in flight, where there is nothing to explain. The
          click refusal has to live here too, because `pointer-events-none`
          stops the mouse and not the keyboard. */}
      <Button className={PHONE_TAP} variant="primary"
        disabled={saveMutation.isPending}
        aria-disabled={timeProblem !== null}
        aria-describedby={timeProblem ? timeErrorId : undefined}
        onClick={() => {
          if (timeProblem) return
          saveMutation.mutate()
        }}
      >
        {saveMutation.isPending ? 'Saving…' : 'Save preferences'}
      </Button>
    </Card>
  )
}

/** The coach-level back-to-back wish (spec D4): a coach with more than one
 *  team can ask for their practices to be back to back (one trip) or on
 *  separate days. Unlike everything in `PracticePrefsForm`, this is NOT
 *  team-scoped, so it is its own component with its own query/mutation
 *  against `/coach/practice-pairing` (Task 3) - rendered ONCE by the route
 *  wrapper below (via `TeamScope`'s `headerExtra`, above the per-team cards),
 *  not once per team card. It saves on change rather than needing its own
 *  "Save" button, so it can never be confused with any one team's own save
 *  action. Renders nothing - and never fetches - for a single-team coach,
 *  since the question is meaningless with one team.
 *
 *  Nor for a SUPERADMIN viewing as a coach. `teamCount` comes from
 *  `GET /api/coach/teams`, which hands an operator every team in the season,
 *  so the count alone would show them a question headed "You coach more than
 *  one team" with help text "This applies to all of your teams" - both false -
 *  and answering it would write a `practice_coach_pairing` row under the
 *  OPERATOR's email, then apply the lit filter to every team they inspect. */
export function PairingCard({ teamCount }: { teamCount: number }) {
  const isAdmin = useIsAdmin()
  const asksPairing = teamCount > 1 && !isAdmin
  const toast = useToast()
  const queryClient = useQueryClient()
  const pairingQ = useQuery({
    queryKey: ['practice-pairing'],
    queryFn: api.practicePairing,
    enabled: asksPairing,
  })
  const mutation = useMutation({
    mutationFn: (pairing: PracticePairing | null) => api.putPracticePairing(pairing),
    onSuccess: () => {
      toast.success('Practice pairing saved.')
      void queryClient.invalidateQueries({ queryKey: ['practice-pairing'] })
    },
    onError: (err: unknown) => toastSaveError(toast, err, {
      // No team (this wish is coach-level), so the fallback has to name the
      // wish itself - see the ⚠️ on toastSaveError.
      fallback: 'Could not save your back-to-back practice preference.',
    }),
  })

  if (!asksPairing) return null

  return (
    <Card as="section">
      <PairingField pairing={pairingQ.data?.pairing ?? null} onChange={(v) => mutation.mutate(v)} />
    </Card>
  )
}

/** Thin route wrapper: resolves the team(s) (via `TeamScope`) and how many
 *  teams this coach has this season, then renders the coach-level pairing
 *  question once - via `TeamScope`'s `headerExtra`, above the per-team cards,
 *  never once per team - plus each team's own prefs form. The team count
 *  rides the same `['teams']` query `TeamScope` already runs, so this adds no
 *  extra fetch - only a second reader of the cached result. */
function Practice() {
  const teamsQ = useQuery({ queryKey: ['teams'], queryFn: api.teams })
  const teamCount = teamsQ.data?.teams.length ?? 0

  return (
    <TeamScope
      heading={useNavTitle()}
      emptyText="No teams found for your account this season."
      hint="Pick a team to set its practice preferences."
      headerExtra={<PairingCard teamCount={teamCount} />}
    >
      {(team) => <PracticePrefsForm team={team} teamCount={teamCount} />}
    </TeamScope>
  )
}
